import {
  Optional,
  Position,
  Size,
} from '@core/models/global-entities/global-entities';
import {
  concat,
  Dictionary,
  isEmpty,
  isEqual,
  isString,
  keyBy,
  omit,
  union,
} from 'lodash';
import { DateTime } from 'luxon';

export function safeJSONParse<T = any>(json: string): Optional<T> {
  let parsed: T;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    console.error('JSON Parsing error:', e);
  }
  return parsed;
}

export function ValidateEmail(emailArr: String[]) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  let emailsValid = true;
  for (let each of emailArr) {
    if (!re.test(String(each).toLowerCase())) emailsValid = false;
  }
  return emailsValid;
}

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  else return '';
}
export function difference<T = any>(
  pureModel: T,
  dirtyModel: T,
  result: T,
  ignoreKeys: string[]
): T {
  ignoreKeys = ignoreKeys || [];
  Object.assign(result, {});
  const simplePure = omit<any>(pureModel, ignoreKeys);
  const simpleDirty = omit<any>(dirtyModel, ignoreKeys);
  const allSimpleKeys = union(
    Object.keys(simplePure),
    Object.keys(simpleDirty)
  );

  allSimpleKeys.forEach((key) => {
    const pureValue = simplePure[key];
    const dirtyValue = simpleDirty[key];
    // if (dirtyValue instanceof DateTime) {
    //   if (dirtyValue && !dirtyValue.equals(pureValue)) {
    //     result[key] = dirtyValue;
    //   }
    // }

    // if string, then use lower case
    // otherwise assetServiceType and companyType are not matching
    if (isString(pureValue) && isString(dirtyValue)) {
      if (pureValue.toLowerCase() !== dirtyValue.toLowerCase()) {
        result[key] = dirtyValue;
      }
    } else if (!isEqual(pureValue, dirtyValue)) {
      result[key] = dirtyValue;
    }
  });
  return result;
}

export function differenceCollection<T extends Object = any>(
  pureCollection: T[],
  dirtyCollection: T[],
  ignoreKeys: string[],
  primaryKey: string
): Partial<T>[] {
  const simplePure = (pureCollection || []).map((o) => omit(o, ignoreKeys));
  const simpleDirty = (dirtyCollection || []).map((o) => omit(o, ignoreKeys));
  const newItems = simpleDirty.filter((o) => !o[primaryKey]);
  const existingDirtyItems = simpleDirty.filter((o) => o[primaryKey]);
  const indexedPureItems: Dictionary<Partial<T>> = keyBy(
    simplePure,
    primaryKey
  );
  const changes = existingDirtyItems
    .map((o) => difference(indexedPureItems[o[primaryKey]], o, {}, ignoreKeys))
    .filter((o) => !isEmpty(o) && !o[primaryKey]);
  // add newly created items
  const result = concat(newItems, changes);
  return result;
}

export function convertTimeStringToObject(
  timeString: string
): { hour: number; minute: number } | null {
  if (!timeString) {
    return;
  }
  // const toParse = timeString.trim().slice(0, 8); // 10:10 AM
  // const isPM = toParse.slice(-2).toLowerCase() === 'pm';
  // const pmHrs = isPM ? 12 : 0;
  // const timePart = toParse.slice(0, -2).trim();
  const timePart = DateTime.fromFormat(
    '12/12/2017 ' + timeString,
    'dd/MM/yyyy h:mm a'
  ).toFormat('HH:mm');
  const [hour, minute] = (timePart || '00:00').split(':');
  return {
    hour: isNaN(+hour) ? 0 : parseInt(hour, 10),
    minute: isNaN(+minute) ? 0 : parseInt(minute, 10),
  };
}

export function updateTimeFromDate(inputDate: Date | DateTime) {
  const inputDateTime =
    inputDate instanceof Date ? DateTime.fromJSDate(inputDate) : inputDate;
  return inputDateTime.toLocaleString(DateTime.TIME_24_SIMPLE);
}

export function toOptionalNumber(data, asFloat = false): Optional<number> {
  const converted = asFloat ? parseFloat(data) : parseInt(data, 10);
  return isNaN(converted) ? null : converted;
}

export function limitPositionToWindow(
  initialPosition: Position,
  popupSize: Size,
  document
): Position {
  if (!document || !document.body) {
    return initialPosition;
  }
  const target: Position = initialPosition;
  const windowRect = document.body.getBoundingClientRect();
  // check bottom
  if (initialPosition.top + popupSize.height > windowRect.height) {
    target.top = windowRect.height - popupSize.height;
  }
  // check right
  if (initialPosition.left + popupSize.width > windowRect.width) {
    target.left = windowRect.width - popupSize.width;
  }
  return target;
}

export function isDarkLight(cssColor: string) {}

export function SortList(list: any[], property: string) {
  if (list) {
    list.sort(function (a, b) {
      let x = a[property].toLowerCase();
      let y = b[property].toLowerCase();
      if (x < y) {
        return -1;
      }
      if (x > y) {
        return 1;
      }
      return 0;
    });
    //return list;
  } else {
    return [];
  }
}

export function isFullMatch(src: string, regex: RegExp): boolean {
  if (!src) {
    return false;
  }
  if (!regex) {
    return true;
  }
  const match = src.match(regex);
  return match && match[0] && match[0].length === src.length;
}

export function deleteAllKeys<T>(obj: T): T {
  Object.keys(obj).forEach(function (key) {
    delete obj[key];
  });
  return obj;
}

export function setTimeoutPromise<T>(promiseFunc: () => Promise<T>, delayInMS) {
  let timeoutRef: NodeJS.Timeout;
  const promise = new Promise((resolve, reject) => {
    timeoutRef = setTimeout(() => {
      promiseFunc().then(resolve).catch(reject);
    }, delayInMS);
  });
  return { promise, timeoutRef };
}
